/**
 * Configuration API client — loan products, payees (identity/contact only),
 * and the org's singleton base rate config (the six commission rates the run
 * screen pre-fills from).
 *
 * Org-scoped like the rest of the app: the caller supplies the org UUID from
 * auth state. All resources are addressed by UUID. Mirrors the shape and
 * conventions of commissionReportsApi.js.
 */
import api from './client';

const base = (orgUuid) => `/organisations/${orgUuid}`;

export const configApi = {
    // ── Products ─────────────────────────────────────────────────────────

    listProducts(orgUuid) {
        return api.get(`${base(orgUuid)}/products`).then((r) => r.data);
    },

    getProduct(orgUuid, productUuid) {
        return api.get(`${base(orgUuid)}/products/${productUuid}`).then((r) => r.data);
    },

    /**
     * @param product { name, code, funder, funderUpfrontRate, funderTrailRate }
     *   rates are decimal fractions (1% = 0.01)
     */
    createProduct(orgUuid, product) {
        return api.post(`${base(orgUuid)}/products`, product).then((r) => r.data);
    },

    updateProduct(orgUuid, productUuid, product) {
        return api.put(`${base(orgUuid)}/products/${productUuid}`, product).then((r) => r.data);
    },

    deleteProduct(orgUuid, productUuid) {
        return api.delete(`${base(orgUuid)}/products/${productUuid}`).then((r) => r.data);
    },

    // ── Payees (identity / contact only) ─────────────────────────────────

    /**
     * @param type optional 'SUBCONTRACTOR' | 'REFERRER' to filter
     */
    listPayees(orgUuid, type) {
        return api
            .get(`${base(orgUuid)}/payees`, { params: type ? { type } : {} })
            .then((r) => r.data);
    },

    getPayee(orgUuid, payeeUuid) {
        return api.get(`${base(orgUuid)}/payees/${payeeUuid}`).then((r) => r.data);
    },

    /**
     * @param payee { type, name, office, code, contactName, contactEmail, contactPhone }
     */
    createPayee(orgUuid, payee) {
        return api.post(`${base(orgUuid)}/payees`, payee).then((r) => r.data);
    },

    updatePayee(orgUuid, payeeUuid, payee) {
        return api.put(`${base(orgUuid)}/payees/${payeeUuid}`, payee).then((r) => r.data);
    },

    deletePayee(orgUuid, payeeUuid) {
        return api.delete(`${base(orgUuid)}/payees/${payeeUuid}`).then((r) => r.data);
    },

    // ── Rate config (singleton per org) ──────────────────────────────────

    /**
     * The org's saved base rates. Returns an object whose fields are null
     * when nothing has been saved yet, so the UI can render a blank form.
     */
    getRateConfig(orgUuid) {
        return api.get(`${base(orgUuid)}/rate-config`).then((r) => r.data);
    },

    /**
     * Create or update the org's singleton rate config.
     * @param config {
     *   funderUpfrontRate, funderTrailRate,
     *   subbyUpfrontRate, subbyTrailRate,
     *   referrerUpfrontRate, referrerTrailRate
     * } decimal fractions (1% = 0.01)
     */
    saveRateConfig(orgUuid, config) {
        return api.put(`${base(orgUuid)}/rate-config`, config).then((r) => r.data);
    },
};